/**
 * TopMoversCard — two tiny side-by-side tables: 5 biggest gainers 🟢 and
 * 5 biggest drops 🔴 across the user's portfolio this week. Each row is
 * clickable and jumps straight into the client's rank-tracker tab.
 */
import React from "react";
import { useNavigate } from "react-router-dom";
import { TrendingUp, TrendingDown, ArrowUpRight } from "lucide-react";

function Row({ it, kind }) {
  const navigate = useNavigate();
  const positive = kind === "up";
  return (
    <button
      type="button"
      onClick={() => navigate(`/clients/${it.client_id}?tab=rank-tracker`)}
      data-testid={`mover-${kind}-${it.keyword}`}
      className="w-full flex items-center gap-3 px-3.5 py-2.5 hover:bg-[#f7fafc] transition-colors text-left group"
    >
      <div className={`font-mono-tight text-sm font-bold tabular-nums ${positive ? "text-[#2c7a7b]" : "text-[#c05621]"} min-w-[46px]`}>
        {positive ? "+" : ""}{it.delta}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm text-[#1a202c] truncate font-semibold leading-snug" title={it.keyword}>{it.keyword}</div>
        <div className="text-[11px] text-[#718096] truncate font-mono-tight mt-0.5">
          {it.client_name} · {it.previous_position} → {it.current_position}
        </div>
      </div>
      <ArrowUpRight className="h-3.5 w-3.5 text-[#a0aec0] opacity-0 group-hover:opacity-100 transition-opacity flex-none" />
    </button>
  );
}

function Side({ title, icon: Icon, items, tone, kind, emptyLabel }) {
  return (
    <div className="card-soft overflow-hidden">
      <div className="px-3 py-2.5 border-b border-[#edf2f7] flex items-center gap-2">
        <div className={`h-5 w-5 rounded-md ${tone.bg} flex items-center justify-center`}>
          <Icon className={`h-3 w-3 ${tone.icon}`} strokeWidth={2.4} />
        </div>
        <div className="text-[10px] uppercase tracking-widest text-[#4a5568] font-bold">{title}</div>
      </div>
      {items.length === 0 ? (
        <div className="px-3 py-6 text-[11px] text-[#a0aec0] italic text-center">{emptyLabel}</div>
      ) : (
        <div className="divide-y divide-[#edf2f7]">
          {items.map((it, i) => <Row key={`${it.client_id}-${it.keyword}-${i}`} it={it} kind={kind} />)}
        </div>
      )}
    </div>
  );
}

export default function TopMoversCard({ data, loading }) {
  if (loading || !data) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="top-movers-loading">
        {Array.from({ length: 2 }).map((_, i) => (
          <div key={i} className="card-soft p-4 animate-pulse h-[250px]" />
        ))}
      </div>
    );
  }
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="top-movers-card">
      <Side
        title="Biggest gains this week"
        icon={TrendingUp}
        items={data.gainers || []}
        tone={{ bg: "bg-[#4fd1c5]/15", icon: "text-[#2c7a7b]" }}
        kind="up"
        emptyLabel="No movers yet — rank data accrues over time."
      />
      <Side
        title="Biggest drops this week"
        icon={TrendingDown}
        items={data.losers || []}
        tone={{ bg: "bg-[#fed7d7]", icon: "text-[#c53030]" }}
        kind="down"
        emptyLabel="No drops this week."
      />
    </div>
  );
}
